import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import FadeIn from "react-fade-in";

import { UserContext } from "../../../storage/contexts/UserContext";
import {
  GET_ORGANISATION_MOOD_DATA,
  GET_ORGANISATION_MOOD_DATA_CSV,
} from "../../../Routes";
import { generateURLOptions } from "../../../URLOptions";

import sad from "../../../assets/images/moods/Sad.png";
import unhappy from "../../../assets/images/moods/Unhappy.png";
import okay from "../../../assets/images/moods/Neutral.png";
import happy from "../../../assets/images/moods/Happy.png";
import elated from "../../../assets/images/moods/Excited.png";
import CommonDatePicker from "../../../InterstellrComponents/CommonDatePicker";
import moment from "moment";
import FilledButton from "../../../InterstellrComponents/FilledButton";

const ManagerCal = () => {
  const { profile } = useContext(UserContext);

  const [moodData, setMoodData] = useState([]);

  const [statusMessage, setStatusMessage] = useState(
    "Updating Dashboard Statistics"
  );

  const [isLoading, setIsLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(
    moment().startOf("day").toDate()
  );

  function handleDateChange(date) {
    setStatusMessage("Updating Dashboard Statistics");
    setIsLoading(true);

    const serverDate = moment(date).startOf("day").toDate();

    setSelectedDate(serverDate);
    getCheckinData(serverDate);
  }

  useEffect(() => {
    getCheckinData(selectedDate);
  }, []);

  const getCheckinData = async (date) => {
    await axios
      .post(
        GET_ORGANISATION_MOOD_DATA,
        { date: date },
        { headers: generateURLOptions() }
      )
      .then(async (response) => {
        console.log(response);
        if (response?.data?.success == false) {
          return alert(response?.data?.message ?? "");
        }

        setMoodData(response.data.data);
        setTimeout(() => {
          setIsLoading(false);
        }, 250);
      })
      .catch((error) => {
        console.log("Organisation Pulse Capture Error: ", error);
        return alert(error);
      });
  };

  const downloadCSV = async (date) => {
    try {
      const response = await axios.post(
        GET_ORGANISATION_MOOD_DATA_CSV,
        { date },
        {
          headers: generateURLOptions(),
          responseType: "blob", // 🔥 IMPORTANT
        }
      );

      // Create a downloadable URL
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);

      // Create a temporary link and click it
      const link = document.createElement("a");
      link.href = url;
      link.download = `mood_calendar_${date}.csv`; // 🔥 filename
      document.body.appendChild(link);
      link.click();

      // Cleanup
      link.remove();
      window.URL.revokeObjectURL(url);

      setTimeout(() => {
        setIsLoading(false);
      }, 250);
    } catch (error) {
      console.log("CSV Download Error: ", error);
      alert("Failed to download CSV");
    }
  };

  return (
    <>
      <div className="relative">
        <div
          className="z-0 absolute top-0 bottom-0 left-0 right-0"
          style={{
            background: `linear-gradient(45deg, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0.8) 45%, ${
              profile?.company_id
                ? profile?.company_id?.company_primary_color
                : "rgba(81, 83, 245, 1)"
            } 150%)`,
          }}
        />

        {!isLoading ? (
          <FadeIn className="overflow-hidden" delay={250}>
            <div className="relative h-[90vh] z-[20] pt-12 w-full md:w-2/3 mx-auto px-6 md:px-0 pb-12">
              {/* Search Header */}
              <div className="flex flex-row justify-center items-start">
                <h4 className="font-medium text-xl">Organisational Pulse</h4>

                <div style={{ flex: 1 }} />

                <div className="w-1/5 mr-4">
                  <FilledButton
                    text={"Export CSV"}
                    onPress={() => downloadCSV(selectedDate)}
                  />
                </div>

                <CommonDatePicker
                  value={selectedDate}
                  onChange={handleDateChange}
                />
              </div>

              <div className="h-[70vh] grid grid-cols-2 md:grid-cols-5 gap-8 md:gap-2 mb-8 mt-12 md:mt-0">
                {/* Sad Item */}
                <div className="flex flex-col justify-center items-center">
                  <img
                    src={sad}
                    className="h-32 md:h-40 w-32 md:w-40 object-contain"
                  />
                  <h6 className="text-center text-black text-base font-semibold mt-4">
                    Sad
                  </h6>
                  <p className="text-center text-black text-base font-normal mt-0">
                    {moodData.sad?.percentage
                      ? Number(moodData.sad?.percentage).toFixed(2)
                      : "0"}{" "}
                    %
                  </p>
                </div>

                {/* Unhappy item */}
                <div className="flex flex-col justify-center items-center">
                  <img
                    src={unhappy}
                    className="h-32 md:h-40 w-32 md:w-40 object-contain"
                  />
                  <h6 className="text-center text-black text-base font-semibold mt-4">
                    Unhappy
                  </h6>
                  <p className="text-center text-black text-base font-normal mt-0">
                    {moodData.unhappy?.percentage
                      ? Number(moodData.unhappy?.percentage).toFixed(2)
                      : "0"}{" "}
                    %
                  </p>
                </div>

                {/* Okay Item */}
                <div className="flex flex-col justify-center items-center">
                  <img
                    src={okay}
                    className="h-32 md:h-40 w-32 md:w-40 object-contain"
                  />
                  <h6 className="text-center text-black text-base font-semibold mt-4">
                    Okay
                  </h6>
                  <p className="text-center text-black text-base font-normal mt-0">
                    {moodData.okay?.percentage
                      ? Number(moodData.okay?.percentage).toFixed(2)
                      : "0"}{" "}
                    %
                  </p>
                </div>

                {/* Happy Item */}
                <div className="flex flex-col justify-center items-center">
                  <img
                    src={happy}
                    className="h-32 md:h-40 w-32 md:w-40 object-contain"
                  />
                  <h6 className="text-center text-black text-base font-semibold mt-4">
                    Happy
                  </h6>
                  <p className="text-center text-black text-base font-normal mt-0">
                    {moodData.happy?.percentage
                      ? Number(moodData.happy?.percentage).toFixed(2)
                      : "0"}{" "}
                    %
                  </p>
                </div>

                {/* Elated Item */}
                <div className="flex flex-col justify-center items-center md:col-auto col-span-full">
                  <img
                    src={elated}
                    className="h-32 md:h-40 w-32 md:w-40 object-contain"
                  />
                  <h6 className="text-center text-black text-base font-semibold mt-4">
                    Elated
                  </h6>
                  <p className="text-center text-black text-base font-normal mt-0">
                    {moodData.elated?.percentage
                      ? Number(moodData.elated?.percentage).toFixed(2)
                      : "0"}{" "}
                    %
                  </p>
                </div>
              </div>
            </div>
          </FadeIn>
        ) : (
          <FadeIn className="overflow-hidden">
            <div className="h-[90vh] relative flex flex-col justify-center items-center z-50">
              <div
                className="h-10 w-10 rounded-full border-4 border-mpPurple/30 border-t-mpPurple animate-spin"
                style={{ marginTop: 12, marginBottom: 12 }}
              />

              <h6 className="text-black font-medium">{statusMessage}</h6>
            </div>
          </FadeIn>
        )}
      </div>
    </>
  );
};

export default ManagerCal;
